import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBox from '../components/desktop/SearchBox';
import resolveAvatar from '../lib/resolveAvatar';

import { fetchWithAuth } from '../lib/fetchWithAuth';
export default function Search() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);

  const q = searchParams.get('q') || '';
  const type = (searchParams.get('type') || 'all') as 'all' | 'users' | 'communities';
  const hasQuery = q.trim().length >= 2;

  useEffect(() => {
    async function search() {
      if (!hasQuery) {
        setUsers([]);
        setCommunities([]);
        return;
      }

      setLoading(true);
      try {
        if (type === 'all') {
          const response = await fetchWithAuth(`/api/search/all?q=${encodeURIComponent(q)}`, {
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            setUsers(data.people || []);
            setCommunities(data.communities || []);
          }
        } else if (type === 'users') {
          const response = await fetchWithAuth(`/api/search/people?q=${encodeURIComponent(q)}`, {
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            setUsers(data.people || []);
          }
          setCommunities([]);
        } else if (type === 'communities') {
          const response = await fetchWithAuth(`/api/search/communities?q=${encodeURIComponent(q)}`, {
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            setCommunities(data.communities || []);
          }
          setUsers([]);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }

    search();
  }, [q, type, hasQuery]);

  return (
    <div className="mx-auto max-w-3xl p-4 space-y-6">
      <SearchBox initialQuery={q} initialType={type} />

      {!hasQuery ? (
        <p className="text-sm text-muted-foreground">
          Enter at least 2 characters and press "Search".
        </p>
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Searching...</p>
      ) : (
        <>
          {type !== 'communities' && (
            <section>
              <h2 className="mb-3 text-lg font-semibold">Users</h2>
              {users.length === 0 ? (
                <p className="text-sm text-muted-foreground">No users found.</p>
              ) : (
                <ul className="space-y-3">
                  {users.map((u) => (
                    <li key={u.id} className="flex items-center gap-3 rounded-lg border p-3">
                      {resolveAvatar(u.avatar) ? (
                        <img
                          src={resolveAvatar(u.avatar)}
                          alt={u.displayName}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-gray-300" />
                      )}
                      <div className="min-w-0">
                        <div className="truncate font-medium">{u.displayName}</div>
                        <div className="truncate text-sm text-muted-foreground">@{u.username}</div>
                        {u.bio && (
                          <div className="truncate text-sm text-muted-foreground mt-1">
                            {u.bio}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {type !== 'users' && (
            <section>
              <h2 className="mb-3 text-lg font-semibold">Communities</h2>
              {communities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No communities found.</p>
              ) : (
                <ul className="space-y-3">
                  {communities.map((c) => (
                    <li key={c.id} className="flex items-center gap-3 rounded-lg border p-3">
                      {resolveAvatar(c.avatar) ? (
                        <img
                          src={resolveAvatar(c.avatar)}
                          alt={c.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-gray-300" />
                      )}
                      <div className="min-w-0">
                        <div className="truncate font-medium">{c.name}</div>
                        {c.description && (
                          <div className="truncate text-sm text-muted-foreground">
                            {c.description}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          {c.accessType} • {c.memberCount} members
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
